//Built-in libraries

import { InformationCircleIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
// third party library
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import HookMaskedInput from '@/components/UI/HookForm/Form/MaskedInput';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';
//Assets and Miscellaneous
import { States } from '../../../../../../constants';
import Scale from '../HookScale';
import AddPropertyForm from './AddPropertyForm';
import { clientType, initialValues, race, rangeOneToFive } from './constants';
import type { IOwnedProperty } from './types';

//Component Begin

export default function ClientInformation() {
  const { updateData, data } = useApplicationFormContext();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [propertyAddress, setPropertyAddress] = useState(initialValues);
  const [selectedAddress, setSelectedAddress] = useState<IOwnedProperty>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editAddressId, setEditAddressId] = useState<string>('');
  const { register, setValue } = useFormContext();

  useEffect(() => {
    register('step1.section4.ownedProperties', undefined);
  }, [register]);

  const removeAddressHandler = (address: IOwnedProperty) => {
    setShowRemoveConfirm(true);
    setSelectedAddress(address);
  };

  const confirmRemoveAddress = async () => {
    const filteredProperties = data?.step1.section4.ownedProperties.filter((t: any) => t.id !== selectedAddress?.id);
    setValue('step1.section4.ownedProperties', filteredProperties);
    updateData({
      ...data,
      step1: {
        ...data.step1,
        section4: {
          ...data.step1.section4,
          ownedProperties: filteredProperties,
        },
      },
    });
    setShowRemoveConfirm(false);
  };

  const cancelRemoveAddress = () => {
    setShowRemoveConfirm(false);
  };

  const toggleAddModal = () => {
    resetForm();
    setShowAddModal((prev) => !prev);
  };

  const resetForm = () => {
    setPropertyAddress(initialValues);
    setEditAddressId('');
  };

  const inputChangedHandler = createInputChangedHandler(setPropertyAddress, propertyAddress);

  const validateForm = () => {
    const formData = { ...propertyAddress };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setPropertyAddress((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddPropertyHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(propertyAddress).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: propertyAddress[next].value };
        },
        {}
      );

      const ownedProperties = [...(data?.step1.section4.ownedProperties || [])];
      ownedProperties.push({ id: uuidv4(), ...formattedData });
      setValue('step1.section4.ownedProperties', ownedProperties);
      updateData({
        step1: {
          ...data.step1,
          section4: {
            ...data.step1.section4,
            ownedProperties,
          },
        },
      });
      resetForm();
      toggleAddModal();
    }
  };

  const editAddressHandler = (address: any) => {
    toggleAddModal();
    setIsEditing(true);
    const formData = Object.keys(propertyAddress).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...propertyAddress[next],
          value: address[next],
          isTouched: !!address[next],
        },
      };
    }, {});

    setPropertyAddress(formData);
    setEditAddressId(address.id);
  };

  const updatePropertyHandler = () => {
    toggleAddModal();
    if (validateForm()) {
      const formattedData = Object.keys(propertyAddress).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: propertyAddress[next].value,
          };
        },
        {}
      );

      const ownedPropertiesUpdated = data.step1.section4.ownedProperties?.map((op: any) => {
        if (op.id === editAddressId) {
          return { id: op.id, ...formattedData };
        }
        return op;
      });
      setValue('step1.section4.ownedProperties', ownedPropertiesUpdated);
      updateData({
        ...data,
        step1: {
          ...data.step1,
          section4: {
            ...data.step1.section4,
            ownedProperties: ownedPropertiesUpdated,
          },
        },
      });

      resetForm();
      setIsEditing(false);
    }
  };
  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Client Information</h3>
      <div className='space-y-10'>
        <section className='space-y-2'>
          <h4 className='font-semibold'>General Information</h4>
          <div className='grid grid-cols-6 gap-6'>
            <HookInput customClassName='col-span-2' label='Name' name='step1.section1.name' type='text' />
            <HookInput customClassName='col-span-2' label='Date of Birth' name='step1.section1.dob' type='date' />
            <HookSelect
              customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
              label='Race'
              name='step1.section1.race'
              options={race.map((s) => ({ label: s, value: s }))}
            />
            <HookMaskedInput customClassName='col-span-2' label='Phone' name='step1.section1.phone' maskedComponent='phone' />
            <HookInput customClassName='col-span-2' label='Email' name='step1.section1.email' type='email' />
            <HookSelect
              customClassName='col-span-6 sm:col-span-3 lg:col-span-2'
              label='Client Type'
              name='step1.section1.clientType'
              options={clientType.map((s) => ({
                label: s,
                value: s,
              }))}
            />
          </div>
        </section>
        <section className='space-y-2'>
          <h4 className='font-semibold'>Current Residence</h4>
          <div className='grid grid-cols-6 gap-6'>
            <HookInput customClassName='col-span-6' label='Street Address' name='step1.section2.streetAddress' type='text' />

            <HookInput customClassName='col-span-2' label='City' name='step1.section2.city' type='text' />
            <HookSelect
              customClassName='col-span-2'
              label='State / Province'
              name='step1.section2.state'
              options={States.map((s) => ({
                label: s.name,
                value: s.name,
              }))}
            />

            <HookInput customClassName='col-span-2' label='Zip / Postal Code' name='step1.section2.zipCode' type='number' />
            <HookInput
              customClassName='col-span-4'
              label='How long have you been at this address (in years)?'
              name='step1.section2.lengthInYears'
              type='number'
            />
            <div className='col-span-2'>
              <HookRadioGroup
                className='flex-col items-start'
                label='Do you own or rent ?'
                name='step1.section2.rentStatus'
                id='step1-section2-rentStatus'
                options={[
                  { label: 'Own', value: 'Own' },
                  { label: 'Rent', value: 'Rent' },
                ]}
              />
            </div>

            <HookRadioGroup
              className='col-span-6'
              label='Are there any outstanding code violations or municipal liens on this property?'
              name='step1.section2.codeViolation'
              id='step1-section2-codeViolation'
              options={[
                { label: 'Yes', value: 'yes' },
                { label: 'No', value: 'no' },
              ]}
            />
          </div>
        </section>
        <section className='space-y-2'>
          <h4 className='font-semibold'>Are the following up to date on this property ?</h4>
          <div className='flex justify-between'>
            <div className='flex flex-col'>
              <HookRadioGroup
                label='Property Taxes'
                name='step1.section3.propertyTaxes'
                id='step1-section3-propertyTaxes'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </div>
            <div className='flex flex-col'>
              <HookRadioGroup
                label='Water Bills'
                name='step1.section3.waterBills'
                id='step1-section3-waterBills'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </div>
            <div className='flex flex-col'>
              <HookRadioGroup
                className='flex-col items-start'
                label='Sewage Bills'
                name='step1.section3.sewageBills'
                id='step1-section3-sewageBills'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </div>
            <div className='flex flex-col'>
              <HookRadioGroup
                className='flex-col items-start'
                label='Refuse Bills'
                name='step1.section3.refuseBills'
                id='step1-section3-refuseBills'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </div>
          </div>
        </section>
        <section className='space-y-2'>
          <div className='flex flex-row my-10 justify-between w-full'>
            <h4 className='font-semibold'>Please list any other properties you own</h4>
            <Button label='Add' type='button' btnStyle='primary' onClick={toggleAddModal} icon={<PlusIcon />} />
          </div>
          {showRemoveConfirm && (
            <Confirm
              title='Remove Property'
              message={`You are about to remove ${selectedAddress?.propertyName} property. Are you sure you want to remove ?`}
              acceptButtonLabel='Remove'
              declineButtonLabel='Cancel'
              accept={confirmRemoveAddress}
              decline={cancelRemoveAddress}
            />
          )}
          {showAddModal && (
            <AddPropertyForm
              isEditing={isEditing}
              address={propertyAddress}
              modalHandler={toggleAddModal}
              inputChangedHandler={inputChangedHandler}
              onAddPropertyHandler={onAddPropertyHandler}
              updatePropertyHandler={updatePropertyHandler}
            />
          )}
          {data?.step1?.section4?.ownedProperties?.length === 0 && (
            <div className='p-2 mt-2 flex flex-col items-center justify-center gap-2 h-2/6'>
              <span>
                <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
              </span>
              <p>No Properties added</p>
            </div>
          )}
          {data?.step1?.section4?.ownedProperties && data?.step1?.section4?.ownedProperties?.length > 0 && (
            <div className='space-y-6 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
              <table className='divide-y divide-gray-200 w-full'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Property Name</th>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Address</th>
                    <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'></th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200 rounded-lg'>
                  {data?.step1?.section4?.ownedProperties?.map((address: IOwnedProperty, _index: number) => (
                    <tr
                      key={address.id}
                      className={`${
                        address.id === editAddressId ? 'bg-neutralColor-lighter' : 'cursor-pointer hover:bg-neutralColor-lighter'
                      }`}
                    >
                      <td className='p-4 capitalize'>{address.propertyName}</td>
                      <td className='p-4 capitalize'>{address.streetAddress}</td>
                      <td className='p-4 flex justify-end gap-4'>
                        <div className=' cursor-pointer' onClick={() => editAddressHandler(address)}>
                          <PencilIcon className='h-5 w-5 text-neutralColor-normal hover:text-secondaryColor-normal' />
                        </div>
                        <div className='cursor-pointer' onClick={() => removeAddressHandler(address)}>
                          <TrashIcon className='h-5 w-5 text-neutralColor-normal hover:text-tertiaryColor-normal' />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className='space-y-2'>
          <h4 className='font-semibold'>Experience with the system</h4>
          <div className='space-y-6'>
            <div className='col-span-6'>
              <HookRadioGroup
                label='Have you ever tried to buy land through the public process before?'
                name='step1.section5.q1'
                id='step1-section5-q1'
                options={[
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ]}
              />
            </div>

            <div className='col-span-6'>
              <Scale
                title='On a scale of 1-5 (5 being the highest), how would you rate your Project Financial Readiness'
                name='step1.section5.projectFinancialReadiness'
                range={rangeOneToFive}
              />
            </div>
            <div className='col-span-6'>
              <Scale
                title='On a scale of 1-5 (5 being the highest), how well do you  understand the process ?'
                name='step1.section5.understandProcess'
                range={rangeOneToFive}
              />
            </div>
          </div>
        </section>
        <section className='space-y-2'>
          <h4 className='font-semibold'>Personal (Organizational) goals</h4>
          <div className='grid grid-cols-6 gap-6'>
            <div className='col-span-6'>
              <Scale
                title='On a scale of 1-5 (5 being the highest), how would you rate your Financial Readiness'
                name='step1.section6.financialReadiness'
                range={rangeOneToFive}
              />
            </div>
            <div className='col-span-6'>
              <Scale
                title='On a scale of 1-5 (5 being the highest), how would you rate your Community Support'
                name='step1.section6.communitySupport'
                range={rangeOneToFive}
              />
            </div>
          </div>
        </section>
      </div>

      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Next' btnStyle='primary' type='submit' />
      </div>
    </div>
  );
}
